import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';

export default function Register() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { register, loginWithGoogle } = useAuth();
  const { googleSsoEnabled } = useFeatures();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 12) {
      newErrors.password = 'Password must be at least 12 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain an uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain a lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain a number';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const result = await register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );
      // In dev mode, the API returns the verification token for testing
      if (result?.verificationToken) {
        setVerificationToken(result.verificationToken);
      }
      setSuccess(true);
    } catch (err) {
      setServerError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const { password } = formData;
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 12) score++;
    if (password.length >= 16) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { score: 1, label: 'Weak', color: '#ef4444' };
    if (score <= 4) return { score: 2, label: 'Fair', color: '#f59e0b' };
    if (score <= 5) return { score: 3, label: 'Good', color: '#22c55e' };
    return { score: 4, label: 'Strong', color: '#059669' };
  };

  const passwordStrength = getPasswordStrength();

  if (success) {
    return (
      <div className="auth-container">
        <style>{`
          .auth-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'DM Sans', system-ui, sans-serif;
            background: linear-gradient(180deg, #faf8f5 0%, #f5f3f0 100%);
            padding: 2rem;
          }

          .success-card {
            background: #fff;
            border-radius: 20px;
            padding: 3rem;
            text-align: center;
            max-width: 420px;
            box-shadow:
              0 1px 3px rgba(0, 0, 0, 0.04),
              0 8px 24px rgba(0, 0, 0, 0.06);
            animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          .success-icon {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
          }

          .success-icon svg {
            width: 40px;
            height: 40px;
            color: #fff;
          }

          .success-title {
            font-family: 'Instrument Serif', Georgia, serif;
            font-size: 2rem;
            color: #1e3a5f;
            margin: 0 0 0.75rem;
          }

          .success-text {
            color: #64748b;
            margin: 0 0 2rem;
            line-height: 1.6;
          }

          .success-btn {
            background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
            color: #fff;
            border: none;
            padding: 1rem 2rem;
            font-size: 1rem;
            font-weight: 600;
            font-family: inherit;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            text-decoration: none;
            display: inline-block;
            box-shadow: 0 2px 8px rgba(12, 74, 110, 0.25);
          }

          .success-btn:hover {
            background: linear-gradient(135deg, #0e5a85 0%, #1a6478 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 16px rgba(12, 74, 110, 0.35);
          }
        `}</style>

        <div className="success-card">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="success-title">Check your email</h1>
          <p className="success-text">
            We've sent a verification link to <strong>{formData.email}</strong>.
            Please check your inbox and click the link to verify your account.
          </p>

          {/* Dev mode: show verification link for testing */}
          {verificationToken && (
            <div style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'left',
            }}>
              <p style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#92400e',
                margin: '0 0 0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Development Mode
              </p>
              <p style={{
                fontSize: '0.875rem',
                color: '#78350f',
                margin: '0 0 0.75rem',
              }}>
                Click below to verify your email (Ethereal inbox or direct link):
              </p>
              <Link
                to={`/verify-email/${verificationToken}`}
                style={{
                  display: 'inline-block',
                  background: '#f59e0b',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  textDecoration: 'none',
                }}
              >
                Verify Email Now
              </Link>
            </div>
          )}

          <Link to="/login" className="success-btn">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <style>{`
        .auth-container {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1fr;
          font-family: 'DM Sans', system-ui, sans-serif;
          background: #faf8f5;
        }

        @media (max-width: 968px) {
          .auth-container {
            grid-template-columns: 1fr;
          }
          .auth-visual {
            display: none;
          }
        }

        /* Visual Panel - Education Theme */
        .auth-visual {
          background: linear-gradient(160deg, #0c4a6e 0%, #164e63 40%, #1e3a5f 100%);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 2.5rem;
        }

        /* Subtle pattern overlay - notebook grid */
        .auth-visual::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 24px 24px;
          z-index: 0;
        }

        /* Warm glow accents */
        .auth-visual::after {
          content: '';
          position: absolute;
          top: -20%;
          right: -20%;
          width: 60%;
          height: 60%;
          background: radial-gradient(circle, rgba(251, 191, 36, 0.15) 0%, transparent 60%);
          z-index: 0;
        }

        .visual-glow-bottom {
          position: absolute;
          bottom: -10%;
          left: -10%;
          width: 50%;
          height: 50%;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, transparent 60%);
          z-index: 0;
        }

        .visual-content {
          position: relative;
          z-index: 1;
        }

        .visual-logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .visual-logo-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
        }

        .visual-logo-icon svg {
          width: 24px;
          height: 24px;
          color: #1e3a5f;
        }

        .visual-logo-text {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.5rem;
          color: #fff;
          font-weight: 400;
        }

        .visual-main {
          position: relative;
          z-index: 1;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 2rem 0;
        }

        .visual-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(251, 191, 36, 0.15);
          border: 1px solid rgba(251, 191, 36, 0.3);
          padding: 0.5rem 1rem;
          border-radius: 100px;
          color: #fcd34d;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 1.5rem;
          width: fit-content;
        }

        .visual-badge svg {
          width: 14px;
          height: 14px;
        }

        .visual-tagline {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: clamp(2rem, 4vw, 2.75rem);
          color: #fff;
          line-height: 1.25;
          max-width: 380px;
          margin-bottom: 1.5rem;
        }

        .visual-tagline em {
          color: #fbbf24;
          font-style: italic;
        }

        .visual-description {
          color: rgba(255, 255, 255, 0.7);
          font-size: 1rem;
          line-height: 1.6;
          max-width: 340px;
        }

        /* Feature highlights */
        .visual-features {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 2rem;
        }

        .visual-feature {
          display: flex;
          align-items: center;
          gap: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
        }

        .visual-feature-icon {
          width: 36px;
          height: 36px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .visual-feature-icon svg {
          width: 18px;
          height: 18px;
          color: #fbbf24;
        }

        .visual-footer {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255,255,255,0.5);
          font-size: 0.8125rem;
        }

        .visual-footer svg {
          width: 16px;
          height: 16px;
          color: #fbbf24;
        }

        /* Form Section */
        .auth-form-section {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: linear-gradient(180deg, #faf8f5 0%, #f5f3f0 100%);
          overflow-y: auto;
        }

        .auth-form-wrapper {
          width: 100%;
          max-width: 420px;
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          padding: 1rem 0;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .mobile-header {
          display: none;
          text-align: center;
          margin-bottom: 2rem;
        }

        @media (max-width: 968px) {
          .mobile-header {
            display: block;
          }
        }

        .mobile-logo {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .mobile-logo-icon {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mobile-logo-icon svg {
          width: 20px;
          height: 20px;
          color: #fbbf24;
        }

        .mobile-logo-text {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.25rem;
          color: #1e3a5f;
        }

        .mobile-tagline {
          color: #64748b;
          font-size: 0.875rem;
        }

        .form-card {
          background: #fff;
          border-radius: 20px;
          padding: 2.5rem;
          box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.04),
            0 8px 24px rgba(0, 0, 0, 0.06);
        }

        .form-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .form-header h1 {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.875rem;
          font-weight: 400;
          color: #1e3a5f;
          margin: 0 0 0.5rem 0;
        }

        .form-header p {
          color: #64748b;
          margin: 0;
          font-size: 0.9375rem;
        }

        .form-header p a {
          color: #0c4a6e;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
        }

        .form-header p a:hover {
          color: #0369a1;
          text-decoration: underline;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 480px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .input-wrapper {
          position: relative;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          font-family: inherit;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: #fff;
          color: #1e3a5f;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .form-input:hover {
          border-color: #cbd5e1;
        }

        .form-input:focus {
          outline: none;
          border-color: #0c4a6e;
          box-shadow: 0 0 0 4px rgba(12, 74, 110, 0.1);
        }

        .form-input::placeholder {
          color: #94a3b8;
        }

        .form-input.has-error {
          border-color: #ef4444;
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
          border-radius: 6px;
        }

        .password-toggle:hover {
          color: #1e3a5f;
          background: #f1f5f9;
        }

        .field-error {
          color: #ef4444;
          font-size: 0.8125rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .password-strength {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.25rem;
        }

        .strength-bars {
          display: flex;
          gap: 4px;
          flex: 1;
        }

        .strength-bar {
          height: 4px;
          flex: 1;
          background: #e2e8f0;
          border-radius: 2px;
          transition: background-color 0.2s;
        }

        .strength-bar.active {
          background: var(--strength-color);
        }

        .strength-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--strength-color);
          min-width: 50px;
          text-align: right;
        }

        .error-message {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 0.875rem 1rem;
          border-radius: 12px;
          font-size: 0.875rem;
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          animation: shake 0.4s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .error-icon {
          flex-shrink: 0;
          margin-top: 1px;
        }

        .submit-btn {
          background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
          color: #fff;
          border: none;
          padding: 1rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          font-family: inherit;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          box-shadow: 0 2px 8px rgba(12, 74, 110, 0.25);
        }

        .submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #0e5a85 0%, #1a6478 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(12, 74, 110, 0.35);
        }

        .submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .terms {
          font-size: 0.8125rem;
          color: #64748b;
          text-align: center;
          line-height: 1.5;
        }

        .terms a {
          color: #0c4a6e;
          text-decoration: underline;
        }

        .terms a:hover {
          color: #0369a1;
        }

        /* Trust badges */
        .trust-section {
          margin-top: 2rem;
          text-align: center;
        }

        .trust-text {
          color: #94a3b8;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.75rem;
        }

        .trust-badges {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          color: #64748b;
        }

        .trust-badge {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8125rem;
        }

        .trust-badge svg {
          width: 16px;
          height: 16px;
          color: #22c55e;
        }

        .google-btn {
          width: 100%;
          background: #fff;
          color: #374151;
          border: 2px solid #e2e8f0;
          padding: 0.875rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          font-family: inherit;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
        }

        .google-btn:hover:not(:disabled) {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .google-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .google-btn svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1.25rem 0;
          color: #94a3b8;
          font-size: 0.8125rem;
        }

        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }
      `}</style>

      <div className="auth-visual">
        <div className="visual-glow-bottom" />

        <div className="visual-content">
          <div className="visual-logo">
            <div className="visual-logo-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <span className="visual-logo-text">rSchdlr</span>
          </div>
        </div>

        <div className="visual-main">
          <div className="visual-badge">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Join Our Community
          </div>
          <h2 className="visual-tagline">
            Begin Your <em>Learning</em> Journey Today
          </h2>
          <p className="visual-description">
            Create your account to access professional development workshops, connect with educators, and track your growth.
          </p>

          <div className="visual-features">
            <div className="visual-feature">
              <div className="visual-feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>Takes less than 2 minutes to sign up</span>
            </div>
            <div className="visual-feature">
              <div className="visual-feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span>Instant access to all events and sessions</span>
            </div>
            <div className="visual-feature">
              <div className="visual-feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span>Your data is secure and protected</span>
            </div>
          </div>
        </div>

        <div className="visual-footer">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Trusted by school districts nationwide
        </div>
      </div>

      <div className="auth-form-section">
        <div className="auth-form-wrapper">
          <div className="mobile-header">
            <div className="mobile-logo">
              <div className="mobile-logo-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="mobile-logo-text">rSchdlr</span>
            </div>
            <p className="mobile-tagline">Professional Development Made Simple</p>
          </div>

          <div className="form-card">
            <div className="form-header">
              <h1>Create account</h1>
              <p>
                Already have an account?{' '}
                <Link to="/login">Sign in</Link>
              </p>
            </div>

            {googleSsoEnabled && (
              <>
                <button
                  type="button"
                  className="google-btn"
                  disabled={googleLoading}
                  onClick={async () => {
                    setGoogleLoading(true);
                    try {
                      await loginWithGoogle('/events');
                    } catch {
                      setGoogleLoading(false);
                    }
                  }}
                >
                  {googleLoading ? (
                    'Redirecting...'
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Sign up with Google
                    </>
                  )}
                </button>
                <div className="divider">or</div>
              </>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              {serverError && (
                <div className="error-message">
                  <svg className="error-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 4.5a1 1 0 112 0v3a1 1 0 11-2 0v-3zm1 7a1 1 0 100-2 1 1 0 000 2z"/>
                  </svg>
                  <span>{serverError}</span>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="firstName">
                    First name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    className={`form-input ${errors.firstName ? 'has-error' : ''}`}
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    autoComplete="given-name"
                    autoFocus
                  />
                  {errors.firstName && (
                    <span className="field-error">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    className={`form-input ${errors.lastName ? 'has-error' : ''}`}
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    autoComplete="family-name"
                  />
                  {errors.lastName && (
                    <span className="field-error">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className={`form-input ${errors.email ? 'has-error' : ''}`}
                  placeholder="you@school.edu"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
                {errors.email && (
                  <span className="field-error">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">
                  Password
                </label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`form-input ${errors.password ? 'has-error' : ''}`}
                    placeholder="Min 12 characters"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                    style={{ paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {formData.password && (
                  <div
                    className="password-strength"
                    style={{ '--strength-color': passwordStrength.color }}
                  >
                    <div className="strength-bars">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`strength-bar ${level <= passwordStrength.score ? 'active' : ''}`}
                        />
                      ))}
                    </div>
                    <span className="strength-label">{passwordStrength.label}</span>
                  </div>
                )}
                {errors.password && (
                  <span className="field-error">{errors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="confirmPassword">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  className={`form-input ${errors.confirmPassword ? 'has-error' : ''}`}
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                {errors.confirmPassword && (
                  <span className="field-error">{errors.confirmPassword}</span>
                )}
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </button>

              <p className="terms">
                By creating an account, you agree to our{' '}
                <a href="/terms">Terms of Service</a> and{' '}
                <a href="/privacy">Privacy Policy</a>.
              </p>
            </form>
          </div>

          <div className="trust-section">
            <div className="trust-text">Your data is protected</div>
            <div className="trust-badges">
              <div className="trust-badge">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Secure
              </div>
              <div className="trust-badge">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                FERPA Compliant
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
